import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResourceManager = ({ onStatsUpdate }) => {
  const [resources, setResources] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [formData, setFormData] = useState({
    title: '', type: '', description: '', file_size: '', file_url: '', published: true
  });
  const { getAuthHeaders } = useAuth();
  const { toast } = useToast();

  useEffect(() => { fetchResources(); }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/admin/resources/items`);
      setResources(response.data);
      onStatsUpdate(response.data.length);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch resources", variant: "destructive" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingResource) {
        await axios.put(`${API}/admin/resources/items/${editingResource.id}`, formData, { headers: getAuthHeaders() });
        toast({ title: "Success!", description: "Resource updated successfully" });
      } else {
        await axios.post(`${API}/admin/resources/items`, formData, { headers: getAuthHeaders() });
        toast({ title: "Success!", description: "Resource created successfully" });
      }
      setDialogOpen(false);
      resetForm();
      fetchResources();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save resource", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      await axios.delete(`${API}/admin/resources/items/${id}`, { headers: getAuthHeaders() });
      toast({ title: "Success!", description: "Resource deleted successfully" });
      fetchResources();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete resource", variant: "destructive" });
    }
  };

  const handleEdit = (resource) => {
    setEditingResource(resource);
    setFormData({ title: resource.title, type: resource.type, description: resource.description, file_size: resource.file_size, file_url: resource.file_url || '', published: resource.published });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingResource(null);
    setFormData({ title: '', type: '', description: '', file_size: '', file_url: '', published: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Resources</h2>
        <Button className="btn-primary text-white" onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />Add New Resource
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {resources.map((resource) => (
          <Card key={resource.id} className="bg-gray-900 border-gray-800">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-white text-lg">{resource.title}</CardTitle>
                  <CardDescription className="text-gray-400">{resource.type} • {resource.file_size}</CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Button size="sm" variant="outline" className="border-gray-700" onClick={() => handleEdit(resource)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-gray-700 text-red-400" onClick={() => handleDelete(resource.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm">{resource.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl">
          <DialogHeader><DialogTitle>{editingResource ? 'Edit Resource' : 'Create New Resource'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input placeholder="Title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-gray-800 border-gray-700 text-white" required />
            <Input placeholder="Type (e.g., PDF, Excel)" value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="bg-gray-800 border-gray-700 text-white" required />
            <Textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-gray-800 border-gray-700 text-white" rows={3} required />
            <Input placeholder="File Size (e.g., 2.3 MB)" value={formData.file_size} onChange={(e) => setFormData({...formData, file_size: e.target.value})} className="bg-gray-800 border-gray-700 text-white" required />
            <Input placeholder="File URL (optional)" value={formData.file_url} onChange={(e) => setFormData({...formData, file_url: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-700">Cancel</Button>
              <Button type="submit" className="btn-primary text-white">{editingResource ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceManager;
